import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Clock, ArrowRight, AlertTriangle, Play } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { resolveImg } from "@/lib/imageUtils";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function LearnerMyCoursesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [mand, setMand] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (mand !== "all") params.mandatory_or_optional = mand;
      const { data } = await api.get("/learner/my-courses", { params });
      setRows(data.data.assignments);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [status, mand]);
  useEffect(() => { load(); }, [load]);

  // Assignments may carry either the string `mandatory_or_optional` (org
  // admin assignments) or the legacy boolean `is_mandatory` (installment/
  // purchase flows). Treat both as the source of truth so purchased courses
  // still surface in the "Optional" section instead of vanishing.
  const isMandatoryRow = (r) => (
    r.mandatory_or_optional === "mandatory" || r.is_mandatory === true
  );
  const mandatoryCourses = rows.filter(isMandatoryRow);
  const optionalCourses = rows.filter((r) => !isMandatoryRow(r));

  return (
    <DashboardLayout title="My Courses" breadcrumbs={["My Learning", "Courses"]}>
      <div className="space-y-6" data-testid="learner-my-courses">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
          <div className="text-sm text-slate-600">Courses assigned to you by your organization.</div>
          <div className="ml-auto flex items-center gap-2">
            <Select value={mand} onValueChange={setMand}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="mandatory">Mandatory</SelectItem>
                <SelectItem value="optional">Optional</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? <LoadingBlock label="Loading your courses…" /> : rows.length === 0 ? (
          <EmptyState title="No courses yet" description="You haven't been assigned any courses. Your admin will enroll you soon." icon={BookOpen} />
        ) : (
          <>
            {mandatoryCourses.length > 0 && (
              <section className="space-y-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-rose-600" />
                  <h3 className="text-sm font-bold text-rose-800 uppercase tracking-widest">Mandatory · {mandatoryCourses.length}</h3>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {mandatoryCourses.map((r) => <CourseCard key={r.id} row={r} />)}
                </div>
              </section>
            )}
            {optionalCourses.length > 0 && (
              <section className="space-y-3">
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-widest">Optional · {optionalCourses.length}</h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {optionalCourses.map((r) => <CourseCard key={r.id} row={r} />)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function CourseCard({ row }) {
  const c = row.course;
  const thumb = c.thumbnail_url || c.thumbnail || c.banner_url;
  const duration = c.duration_hours ?? c.estimated_duration_hours ?? 0;
  const isMandatory = row.mandatory_or_optional === "mandatory" || row.is_mandatory === true;
  return (
    <div className="flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-md transition" data-testid={`learner-course-card-${c.id}`}>
      {thumb ? (
        <img src={resolveImg(thumb)} alt="" className="h-32 w-full object-cover" />
      ) : (
        <div className="h-32 w-full bg-gradient-to-br from-[#101876] to-[#329112] flex items-center justify-center">
          <BookOpen className="h-10 w-10 text-white/70" />
        </div>
      )}
      <div className="flex-1 p-4 space-y-2">
        <div className="flex items-center justify-between gap-2 text-[10px] font-bold uppercase tracking-widest">
          <span className="text-[#329112]">{c.category_name || "Course"}</span>
          {isMandatory && (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-rose-700"><AlertTriangle className="h-3 w-3" /> Mandatory</span>
          )}
        </div>
        <div className="text-sm font-bold text-slate-900 line-clamp-2">{c.course_title}</div>
        <div className="text-xs text-slate-500 flex items-center gap-3">
          <span className="capitalize">{c.course_type?.replace(/_/g, " ")}</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{duration}h</span>
        </div>
        <div className="pt-1 text-xs text-slate-500 flex items-center gap-3">
          <StatusBadge status={row.status} />
          {row.due_date && <span>· Due {row.due_date}</span>}
        </div>
        <div className="pt-1">
          <div className="h-1.5 rounded-full bg-slate-100">
            <div className="h-1.5 rounded-full bg-[#329112]" style={{ width: `${row.progress_percentage || 0}%` }} />
          </div>
          <div className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">{row.progress_percentage || 0}% complete (updates in Phase 6)</div>
        </div>
      </div>
      <Link to={`/app/learner/courses/${row.id}/player`} className="border-t border-slate-100 bg-slate-50 px-4 py-2.5 text-xs font-bold text-[#101876] hover:bg-slate-100 flex items-center justify-between" data-testid={`learner-course-open-${c.id}`}>
        <span className="inline-flex items-center gap-1"><Play className="h-3.5 w-3.5" /> {row.progress_percentage > 0 ? "Resume learning" : "Start learning"}</span>
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
